import React, { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import Pagination from '../components/Pagination';
import { useNavigate } from 'react-router-dom';

const Auditoria = () => {
  const navigate = useNavigate();
  const [auditLogs, setAuditLogs] = useState([]);
  const [isAuditLoading, setIsAuditLoading] = useState(false);
  const [auditSearch, setAuditSearch] = useState('');
  const [filtroAccion, setFiltroAccion] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;

  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const apiBase = isLocal ? `http://${window.location.hostname}:3010` : `${window.location.protocol}//${window.location.hostname}`;

  useEffect(() => {
    const role = currentUser.rol?.toLowerCase();
    const permisos = currentUser.permisos || [];
    // Solo Superadmins o quienes tengan MODULO_ADMIN o GESTIONAR_ROLES pueden ver esto
    if (role !== 'superadmin' && !permisos.includes('MODULO_ADMIN') && !permisos.includes('GESTIONAR_ROLES')) {
      navigate('/dashboard');
      return;
    }
    fetchAuditLogs();
  }, [navigate]);

  const fetchAuditLogs = async () => {
    setIsAuditLoading(true);
    try {
      const resp = await fetch(`${apiBase}/api/admin/audit-logs`, { credentials: 'include' });
      if (resp.ok) {
        setAuditLogs(await resp.json());
      }
    } catch (err) {
      console.error('Error cargando logs de auditoría:', err);
    } finally {
      setIsAuditLoading(false);
    }
  };

  // Clasificar la acción para asignar color e ícono
  const getActionMeta = (accion) => {
    const a = (accion || '').toUpperCase();
    if (a.includes('BLOQUEO') || a.includes('ELIMINAR') || a.includes('SUSPENDER') || a.includes('DELETE'))
      return { color: 'var(--p-rose)', icon: 'fa-shield-halved', label: 'Crítica' };
    if (a.includes('CREAR') || a.includes('REGISTRO') || a.includes('CREATE') || a.includes('INSERT') || a.includes('NUEVO'))
      return { color: 'var(--p-emerald)', icon: 'fa-circle-plus', label: 'Creación' };
    if (a.includes('EDITAR') || a.includes('ACTUALIZAR') || a.includes('UPDATE') || a.includes('MODIFICAR') || a.includes('PERMISOS'))
      return { color: 'var(--p-amber)', icon: 'fa-pen-to-square', label: 'Modificación' };
    if (a.includes('LOGIN') || a.includes('SESION') || a.includes('ACCESO') || a.includes('DESBLOQUE'))
      return { color: 'var(--p-blue)', icon: 'fa-right-to-bracket', label: 'Acceso' };
    return { color: 'var(--p-indigo)', icon: 'fa-clock-rotate-left', label: 'General' };
  };

  // Acciones únicas para el filtro
  const accionesUnicas = [...new Set(auditLogs.map(l => l.accion))].sort();

  const filteredLogs = auditLogs.filter(log => {
    const matchSearch = !auditSearch || 
      log.accion.toLowerCase().includes(auditSearch.toLowerCase()) || 
      log.detalles?.toLowerCase().includes(auditSearch.toLowerCase()) ||
      log.administrador_nombre?.toLowerCase().includes(auditSearch.toLowerCase());
    const matchAccion = !filtroAccion || log.accion === filtroAccion;
    return matchSearch && matchAccion;
  });

  // Stats
  const totalEventos = auditLogs.length;
  const accionesCriticas = auditLogs.filter(l => {
    const a = (l.accion || '').toUpperCase();
    return a.includes('BLOQUEO') || a.includes('ELIMINAR') || a.includes('SUSPENDER') || a.includes('DELETE');
  }).length;
  const adminsUnicos = new Set(auditLogs.map(l => l.administrador_nombre || l.usuario_uid)).size;

  // Pagination
  const totalPages = Math.ceil(filteredLogs.length / itemsPerPage);
  const currentItems = filteredLogs.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  useEffect(() => {
    setCurrentPage(1);
  }, [auditSearch, filtroAccion]);

  // Formatear fecha relativa
  const formatFechaRelativa = (fecha) => {
    const now = new Date();
    const d = new Date(fecha);
    const diffMs = now - d;
    const diffMin = Math.floor(diffMs / 60000);
    const diffHr = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMin < 1) return 'Justo ahora';
    if (diffMin < 60) return `Hace ${diffMin} min`;
    if (diffHr < 24) return `Hace ${diffHr}h`;
    if (diffDays < 7) return `Hace ${diffDays}d`;
    return d.toLocaleDateString('es-CL', { day: '2-digit', month: 'short' });
  };

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <main className="main-content">
        <header className="content-header">
          <div className="header-title">
            <h1>
              <i className="fa-solid fa-clock-rotate-left" style={{ marginRight: '14px', background: 'linear-gradient(135deg, var(--p-indigo), var(--p-blue))', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}></i>
              Auditoría del Sistema
            </h1>
            <p style={{ color: 'var(--p-slate)', fontSize: '14px', marginTop: '4px', fontWeight: '500' }}>
              Registro cronológico de todas las acciones administrativas realizadas en la plataforma.
            </p>
          </div>
        </header>

        {/* Stats Widgets */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px', marginBottom: '30px' }}>
          <div className="widget-card" style={{ borderBottomColor: 'var(--p-indigo)' }}>
            <h3>Total Eventos</h3>
            <p className="big-number">{totalEventos}</p>
          </div>
          <div className="widget-card" style={{ borderBottomColor: 'var(--p-rose)' }}>
            <h3>Acciones Críticas</h3>
            <p className="big-number">{accionesCriticas}</p>
          </div>
          <div className="widget-card" style={{ borderBottomColor: 'var(--p-blue)' }}>
            <h3>Administradores</h3>
            <p className="big-number">{adminsUnicos}</p>
          </div>
        </div>

        <section className="datatable-section animate-fade-in" style={{ marginTop: '0' }}>
          <div className="datatable-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px', gap: '15px' }}>
            <h2 style={{ margin: 0, color: 'var(--p-navy)', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <i className="fa-solid fa-scroll" style={{ color: 'var(--p-indigo)', fontSize: '18px' }}></i>
              Línea de Tiempo
              <span style={{ fontSize: '12px', color: 'var(--p-slate)', fontWeight: '500', marginLeft: '8px' }}>
                {filteredLogs.length} registros
              </span>
            </h2>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <div className="search-bar" style={{ margin: 0 }}>
                <i className="fa-solid fa-magnifying-glass" style={{ position: 'absolute', marginLeft: '15px', marginTop: '16px', color: 'var(--p-slate)' }}></i>
                <input
                  type="text"
                  placeholder="Buscar por usuario, acción o detalles..."
                  value={auditSearch}
                  onChange={(e) => setAuditSearch(e.target.value)}
                  style={{ paddingLeft: '40px', width: '300px', height: '45px' }}
                />
              </div>
              <select
                className="select-premium"
                value={filtroAccion}
                onChange={(e) => setFiltroAccion(e.target.value)}
                style={{ width: '200px', height: '45px' }}
              >
                <option value="">Todas las Acciones</option>
                {accionesUnicas.map(a => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
          </div>

          {isAuditLoading ? (
            <div style={{ textAlign: 'center', padding: '60px 20px' }}>
              <div style={{ width: '48px', height: '48px', borderRadius: '50%', border: '3px solid var(--glass-border)', borderTopColor: 'var(--p-indigo)', margin: '0 auto 16px', animation: 'spin 0.8s linear infinite' }}></div>
              <span style={{ color: 'var(--p-slate)', fontSize: '14px' }}>Cargando registros de auditoría...</span>
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
                {currentItems.map((log, idx) => {
                  const meta = getActionMeta(log.accion);
                  const initials = (log.administrador_nombre || 'S').split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase();
                  return (
                    <div 
                      key={log.id} 
                      style={{
                        display: 'flex',
                        gap: '16px',
                        padding: '18px 20px',
                        borderBottom: '1px solid var(--glass-border)',
                        transition: 'background 0.2s ease',
                        borderRadius: idx === 0 ? '12px 12px 0 0' : idx === currentItems.length - 1 ? '0 0 12px 12px' : '0',
                        cursor: 'default',
                        animation: `fadeSlideIn 0.3s ease backwards ${idx * 0.03}s`
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = 'var(--table-row-hover)'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                    >
                      {/* Timeline dot */}
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0', flexShrink: 0, width: '44px' }}>
                        <div style={{
                          width: '40px', height: '40px', borderRadius: '12px',
                          background: `${meta.color}15`,
                          border: `1.5px solid ${meta.color}30`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          color: meta.color, fontSize: '15px', flexShrink: 0
                        }}>
                          <i className={`fa-solid ${meta.icon}`}></i>
                        </div>
                      </div>

                      {/* Content */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                            {/* Admin avatar */}
                            <div style={{
                              width: '28px', height: '28px', borderRadius: '8px',
                              background: 'linear-gradient(135deg, var(--p-indigo), var(--p-blue))',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              color: 'white', fontSize: '10px', fontWeight: '800', flexShrink: 0
                            }}>
                              {initials}
                            </div>
                            <span style={{ fontWeight: '700', color: 'var(--p-navy)', fontSize: '14px' }}>
                              {log.administrador_nombre || 'Sistema'}
                            </span>
                            <span style={{
                              padding: '3px 10px', borderRadius: '6px', fontSize: '10px', fontWeight: '800',
                              background: `${meta.color}12`, color: meta.color,
                              border: `1px solid ${meta.color}20`, letterSpacing: '0.5px', textTransform: 'uppercase'
                            }}>
                              {log.accion}
                            </span>
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', flexShrink: 0 }}>
                            <span style={{ fontSize: '12px', color: 'var(--p-slate)', fontWeight: '600' }}>
                              {formatFechaRelativa(log.fecha)}
                            </span>
                            <span style={{ fontSize: '10px', color: 'var(--p-slate)', opacity: 0.6 }}>
                              {new Date(log.fecha).toLocaleString('es-CL', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit', year: '2-digit' })}
                            </span>
                          </div>
                        </div>

                        {/* Details */}
                        <div style={{ marginLeft: '38px' }}>
                          {log.detalles && (
                            <p style={{ margin: '0 0 4px', fontSize: '13px', color: 'var(--p-navy)', fontWeight: '500', lineHeight: '1.5' }}>
                              {log.detalles}
                            </p>
                          )}
                          {log.endpoint && (
                            <span style={{ fontSize: '11px', color: 'var(--p-slate)', fontFamily: 'monospace', opacity: 0.7, background: 'var(--sidebar-accent)', padding: '2px 8px', borderRadius: '4px' }}>
                              {log.endpoint}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}

                {filteredLogs.length === 0 && (
                  <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                    <i className="fa-solid fa-magnifying-glass" style={{ fontSize: '40px', color: 'var(--p-slate)', opacity: 0.2, display: 'block', marginBottom: '16px' }}></i>
                    <p style={{ color: 'var(--p-slate)', fontSize: '15px', margin: '0 0 4px', fontWeight: '600' }}>Sin resultados</p>
                    <p style={{ color: 'var(--p-slate)', fontSize: '13px', margin: 0, opacity: 0.7 }}>No hay registros que coincidan con los filtros aplicados.</p>
                  </div>
                )}
              </div>

              <Pagination 
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
              />
            </>
          )}
        </section>
      </main>
    </div>
  );
};

export default Auditoria;
